import { Modal, Box } from '@mui/material';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Grid,
  Pagination,
  Tooltip,
} from "@mui/material";
import instance from "../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import Topbar from "../../Layouts/Admin_layout/Admin_topbar";
import CustomDatePicker from "../../Components/Admin/CustomDatePicker";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { SiMicrosoftexcel } from "react-icons/si";
import { formatDate } from "../../utils/helper";

// Override maplibre popup styling to prevent grey/white boxes
const mapGlobalStyle = document.createElement('style');
mapGlobalStyle.textContent = `
  .maplibregl-map { background: #1a1a2e !important; }
  .maplibregl-canvas-container { background: #1a1a2e !important; }
  .maplibregl-popup-content {
    border-radius: 8px !important;
    box-shadow: 0 2px 10px rgba(0,0,0,0.15) !important;
    padding: 0 !important;
    overflow: hidden !important;
    background: #fff !important;
  }
  .maplibregl-popup-anchor-bottom .maplibregl-popup-tip { border-top-color: #fff !important; }
  .maplibregl-popup-anchor-top .maplibregl-popup-tip { border-bottom-color: #fff !important; }
  .maplibregl-popup-anchor-left .maplibregl-popup-tip { border-right-color: #fff !important; }
  .maplibregl-popup-anchor-right .maplibregl-popup-tip { border-left-color: #fff !important; }
  .maplibregl-popup { max-width: 300px !important; }
  .maplibregl-ctrl-attrib { display: none !important; }
`;
if (!document.getElementById('maplibre-custom-style')) {
  mapGlobalStyle.id = 'maplibre-custom-style';
  document.head.appendChild(mapGlobalStyle);
}

const MAP_STYLES = {
  roadmap: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
  satellite: {
    version: 8,
    sources: {
      satellite: {
        type: 'raster',
        tiles: [
          'https://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
          'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
          'https://mt2.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
          'https://mt3.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'
        ],
        tileSize: 256,
        maxzoom: 20
      }
    },
    layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }]
  }
};

const formatUrlDate = (date) => {
  if (!date) return "";
  return new Intl.DateTimeFormat("en-US").format(date).replaceAll("/", "%2F");
};

const getCombinedTracking = (day) => {
  const points = [...(day.day_tracking || [])];
  if (day.check_in && day.check_in.time) {
    points.push({
      ...day.check_in,
      location: day.check_in.location || "Check In"
    });
  }
  if (day.check_out && day.check_out.time) {
    points.push({
      ...day.check_out,
      location: day.check_out.location || "Check Out"
    });
  }
  return points;
};

function isTimeInRange(time, startTime, endTime) {
  if (!time || !startTime || !endTime) return false;
  const date = new Date();

  let checkHour = 0;
  let checkMinute = 0;

  const isPM = time.toUpperCase().includes("PM");
  const isAM = time.toUpperCase().includes("AM");
  const timeParts = time.replace(/[^\d:]/g, "").split(":");

  if (timeParts.length >= 2) {
    checkHour = parseInt(timeParts[0], 10);
    checkMinute = parseInt(timeParts[1], 10);
    if (isPM && checkHour < 12) checkHour += 12;
    if (isAM && checkHour === 12) checkHour = 0;
  }

  const [startHour, startMinute] = startTime.split(":").map(Number);
  const [endHour, endMinute] = endTime.split(":").map(Number);
  const checkTime = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    checkHour,
    checkMinute
  );
  const start = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    startHour,
    startMinute
  );
  const end = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    endHour,
    endMinute
  );
  return checkTime >= start && checkTime <= end;
}

const format12Hour = (timeStr) => {
  if (!timeStr || timeStr === "Tracking all day") return timeStr;
  
  const isPM = timeStr.toUpperCase().includes("PM");
  const isAM = timeStr.toUpperCase().includes("AM");
  
  const timeParts = timeStr.replace(/[^\d:]/g, "").split(":");
  if (timeParts.length < 2) return timeStr;
  
  let hour = parseInt(timeParts[0], 10);
  const min = timeParts[1];
  
  const ampm = isPM ? "PM" : (isAM ? "AM" : ((hour >= 12 && hour < 24) ? "PM" : "AM"));
  
  if (hour === 0 || hour === 24) {
    hour = 12;
  } else if (hour > 12) {
    hour = hour % 12;
  }
  
  return `${hour}:${min} ${ampm}`;
};

const formatSlotLabel = (slot) => {
  if (slot === "Tracking all day") return slot;
  const parts = slot.split(" to ");
  if (parts.length === 2) {
    return `${format12Hour(parts[0])} to ${format12Hour(parts[1])}`;
  }
  return slot;
};

const generateTimeSlots = () => {
  const slots = [];
  for (let i = 0; i < 24; i++) {
    slots.push(`${i}:00 to ${i}:20`);
    slots.push(`${i}:20 to ${i}:40`);
    slots.push(`${i}:40 to ${i + 1}:00`);
  }
  slots.push("Tracking all day");
  return slots;
};

const getBestTrackInSlot = (dayTracking, start, end) => {
  const tracksInSlot = dayTracking.filter(t => isTimeInRange(t.time, start, end));
  if (tracksInSlot.length === 0) return null;
  // Find the first track that has a valid location name (not null, not undefined, and not empty or "null" string)
  const trackWithLocation = tracksInSlot.find(
    t => t.location && t.location.trim() !== "" && t.location.trim().toLowerCase() !== "null"
  );
  return trackWithLocation || tracksInSlot[0];
};

const getTracksForTimeSlots = (dayData) => {
  const combinedTracking = getCombinedTracking(dayData);
  const slots = generateTimeSlots().filter(slot => slot !== "Tracking all day");
  const uniqueTracks = [];
  const seenTrackKeys = new Set();

  slots.forEach(slot => {
    const [start, end] = slot.split(" to ");
    const track = getBestTrackInSlot(combinedTracking, start, end);
    if (track && track.latitude && track.longitude) {
      const trackKey = `${track.time}-${track.latitude}-${track.longitude}`;
      if (!seenTrackKeys.has(trackKey)) {
        seenTrackKeys.add(trackKey);
        uniqueTracks.push(track);
      }
    }
  });
  return uniqueTracks;
};

function FieldOfficersDetails() {
  const [openMap, setOpenMap] = useState(false);
  const [mapPoints, setMapPoints] = useState([]);
  const [mapType, setMapType] = useState('satellite');
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const popupRef = useRef(null);
  const navigate = useNavigate();
  const [fieldOfficerDetails, setFieldOfficersDetails] = useState([]);
  const [officerTracking, setOfficerTracking] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    currentPage: 1,
  });

  // Open map for a specific day's tracking data only
  const handleOpenMap = (dayData) => {
    const uniqueTracks = getTracksForTimeSlots(dayData);

    const points = uniqueTracks
      .map((track, i) => ({
        lat: parseFloat(track.latitude),
        lng: parseFloat(track.longitude),
        label: track.location || `Point ${i + 1}`,
        time: track.time || ''
      }))
      .filter(point => !isNaN(point.lat) && !isNaN(point.lng) && point.lat !== 0 && point.lng !== 0);
    points.forEach((p, i) => p.index = i + 1);
    setMapPoints(points);
    setOpenMap(true);
  };

  // Open all points in Google Maps with labeled markers
  const handleOpenInGoogleMaps = () => {
    if (mapPoints.length === 0) return;
    if (mapPoints.length === 1) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${mapPoints[0].lat},${mapPoints[0].lng}`, '_blank');
      return;
    }
    const origin = `${mapPoints[0].lat},${mapPoints[0].lng}`;
    const destination = `${mapPoints[mapPoints.length - 1].lat},${mapPoints[mapPoints.length - 1].lng}`;
    const waypointsList = mapPoints.slice(1, -1).map(p => `${p.lat},${p.lng}`).join('|');
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    if (waypointsList) {
      url += `&waypoints=${encodeURIComponent(waypointsList)}`;
    }
    window.open(url, '_blank');
  };

  // Initialize / update map when modal opens or mapType changes
  const initMap = useCallback(() => {
    if (!mapContainerRef.current || mapPoints.length === 0) return;

    // Remove old map
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    markersRef.current = [];

    const center = [mapPoints[0].lng, mapPoints[0].lat];

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLES[mapType],
      center: center,
      zoom: 15,
      maxZoom: 19,
      attributionControl: false
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new maplibregl.FullscreenControl(), 'top-right');

    map.on('load', () => {
      // Group points that are within ~100m of each other (3 decimal places)
      var locationGroups = {};
      mapPoints.forEach(function(point) {
        var key = parseFloat(point.lat).toFixed(3) + ',' + parseFloat(point.lng).toFixed(3);
        if (!locationGroups[key]) locationGroups[key] = [];
        locationGroups[key].push(point);
      });

      // One marker per group
      Object.values(locationGroups).forEach(function(group) {
        var firstPoint = group[0];
        var el = document.createElement('div');
        el.style.cssText = 'width:32px;height:32px;background:#78c144;color:#fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);cursor:pointer;';
        // Single point shows its index, cluster shows LAST point's index
        el.textContent = group.length > 1 ? group[group.length - 1].index : firstPoint.index;

        var marker = new maplibregl.Marker({ element: el })
          .setLngLat([firstPoint.lng, firstPoint.lat])
          .addTo(map);

        el.addEventListener('click', function(e) {
          e.stopPropagation();
          if (popupRef.current) popupRef.current.remove();

          var popupHtml = '';
          if (group.length > 1) {
            popupHtml += '<div style="font-family:sans-serif;padding:8px;width:270px;">';
            popupHtml += '<div style="font-weight:bold;margin-bottom:6px;font-size:13px;color:#333;">' + group.length + ' tracking points here</div>';
            popupHtml += '<div style="font-size:11px;color:#666;margin-bottom:8px;">Lat: ' + firstPoint.lat + ' | Lng: ' + firstPoint.lng + '</div>';
            popupHtml += '<div style="max-height:150px;overflow-y:auto;border:1px solid #ddd;border-radius:4px;background:#fff;">';
            for (var i = 0; i < group.length; i++) {
              var p = group[i];
              var timeStr = p.time ? format12Hour(p.time) : '';
              popupHtml += '<div style="padding:5px 10px;border-bottom:1px solid #eee;font-size:12px;display:flex;justify-content:space-between;align-items:center;">';
              popupHtml += '<span><b style="color:#78c144">#' + p.index + '</b> ' + (p.label || '-') + '</span>';
              if (timeStr) popupHtml += '<span style="color:#888;font-size:11px;white-space:nowrap;margin-left:8px;">' + timeStr + '</span>';
              popupHtml += '</div>';
            }
            popupHtml += '</div></div>';
          } else {
            var p2 = group[0];
            var timeStr2 = p2.time ? format12Hour(p2.time) : '';
            popupHtml += '<div style="padding:8px;font-family:sans-serif;">';
            popupHtml += '<b style="color:#78c144;font-size:14px;">#' + p2.index + '</b><br/>';
            popupHtml += '<span style="font-size:12px;">' + (p2.label || '-') + '</span><br/>';
            popupHtml += '<span style="font-size:11px;color:#666;">Lat: ' + p2.lat + ', Lng: ' + p2.lng + '</span><br/>';
            if (timeStr2) popupHtml += '<span style="font-size:11px;color:#555;">Time: ' + timeStr2 + '</span>';
            popupHtml += '</div>';
          }

          var popup = new maplibregl.Popup({ offset: [0, -18], closeButton: true, maxWidth: '300px', className: 'tracking-popup' })
            .setLngLat([firstPoint.lng, firstPoint.lat])
            .setHTML(popupHtml)
            .addTo(map);
          popupRef.current = popup;
        });

        markersRef.current.push(marker);
      });

      // Green line connecting all points in order
      if (mapPoints.length > 1) {
        map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: mapPoints.map(function(p) { return [p.lng, p.lat]; })
            }
          }
        });
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          paint: {
            'line-color': '#78c144',
            'line-width': 4,
            'line-opacity': 0.8
          }
        });
      }

      // Keep map centered on first point - user can manually zoom out to see all points      // Keep map centered on first point - user can manually zoom out to see all points      // Keep map centered on first point - user can manually zoom out to see all points
    });

    mapRef.current = map;
  }, [mapPoints, mapType]);

  useEffect(() => {
    if (openMap) {
      // Small delay to let modal render
      setTimeout(initMap, 100);
    }
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [openMap, mapType, initMap]);

  // Modal style
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    height: '80vh',
    bgcolor: 'transparent',
    boxShadow: 'none',
    p: 0,
    outline: 'none',
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  // Map modal component
  const MapModal = () => (
    <Modal open={openMap} onClose={() => setOpenMap(false)}>
      <Box sx={modalStyle}>
        {/* Top bar with controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#fff', borderRadius: '4px 4px 0 0' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setMapType('roadmap')}
              style={{
                padding: '6px 14px',
                background: mapType === 'roadmap' ? '#78c144' : '#e0e0e0',
                color: mapType === 'roadmap' ? '#fff' : '#333',
                border: 'none',
                borderRadius: 4,
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Map View
            </button>
            <button
              onClick={() => setMapType('satellite')}
              style={{
                padding: '6px 14px',
                background: mapType === 'satellite' ? '#78c144' : '#e0e0e0',
                color: mapType === 'satellite' ? '#fff' : '#333',
                border: 'none',
                borderRadius: 4,
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Satellite View
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleOpenInGoogleMaps}
              style={{
                padding: '6px 14px',
                background: '#4285F4',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              📍 Open in Google Maps
            </button>
            <button
              onClick={() => setOpenMap(false)}
              style={{
                padding: '6px 14px',
                background: '#f44336',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
        {/* Map Container */}
        <div ref={mapContainerRef} style={{ flex: 1, borderRadius: 8, overflow: 'hidden', background: '#1a1a2e' }} />
        {/* Points count info */}
        <div style={{ padding: '8px 12px', fontSize: '0.9em', color: '#555', background: '#fff', borderRadius: '0 0 4px 4px' }}>
          Total tracking points: <b>{mapPoints.length}</b>
        </div>
      </Box>
    </Modal>
  );

  const { hash } = useParams();
  const tableTrackingRef = useRef(null);
  const [trackingData, setTrackingData] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const handleChange = (event, value) => {
    setPage(value);
  };

  const fetchFieldOfficersDetails = useCallback(async () => {
    try {
      const response = await instance(
        `field_officer/profile?fieldofficer_hash=${hash}`
      );
      setFieldOfficersDetails(response.data.data);
    } catch (err) {
      console.log(err);
    }
  }, [hash]);

  useEffect(() => {
    fetchFieldOfficersDetails();
  }, [fetchFieldOfficersDetails]);

  const fetchFieldOfficersTracking = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      const formattedStartDate = formatUrlDate(startDate);
      const formattedEndDate = formatUrlDate(endDate);
      const response = await instance(
        `/field_officer/getFieldOfficerTracking?start_date=${formattedStartDate}&end_date=${formattedEndDate}&fieldofficer_hash=${hash}&page=${page}`
      );
      setOfficerTracking(response.data.tracking);
      setPagination({
        totalPages: response.data.totalPages || 1,
        currentPage: response.data.currentPage || 1,
      });
      setTrackingData(true);
    } catch (err) {
      console.log(err);
    }
  }, [startDate, endDate, hash, page]);

  useEffect(() => {
    fetchFieldOfficersTracking();
  }, [fetchFieldOfficersTracking]);

  const handleTrackFO = () => {
    if (page === 1) {
      fetchFieldOfficersTracking();
    } else {
      setPage(1);
    }
  };

  useEffect(() => {
    if (trackingData && tableTrackingRef.current) {
      tableTrackingRef.current.scrollIntoView({ behaviour: "smooth" });
      setTrackingData(false);
    }
  }, [trackingData]);

  const timeSlots = generateTimeSlots();

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Field Officer Tracking');

    const brandColor = '78c144';
    const lightGreen = 'e8f5e9';
    const borderStyle = { style: 'thin', color: { argb: 'AAAAAA' } };

    const formatDateStr = (date) => {
      if (!date) return "-";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };

    worksheet.getColumn(1).width = 5;
    worksheet.getColumn(2).width = 25;
    const displaySlots = timeSlots.filter(slot => slot !== "Tracking all day");
    for (let i = 3; i <= displaySlots.length + 2; i++) {
      worksheet.getColumn(i).width = 50;
    }

    worksheet.addRow([]);
    worksheet.mergeCells('B2:H2');
    const mainTitle = worksheet.getCell('B2');
    mainTitle.value = 'FIELD OFFICER TRACKING REPORT';
    mainTitle.font = { bold: true, size: 20, color: { argb: brandColor } };
    mainTitle.alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.getRow(2).height = 35;

    worksheet.mergeCells('B3:H3');
    const rangeCell = worksheet.getCell('B3');
    rangeCell.value = `Period: ${formatDateStr(startDate)} To ${formatDateStr(endDate)}`;
    rangeCell.font = { italic: true, size: 12, color: { argb: '666666' } };
    rangeCell.alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.getRow(3).height = 20;

    worksheet.addRow([]);

    const detailTitleRow = worksheet.addRow(['', 'Field Officer Details']);
    const detailTitleCell = detailTitleRow.getCell(2);
    detailTitleCell.font = { bold: true, size: 14, color: { argb: brandColor } };
    detailTitleCell.alignment = { horizontal: 'left' };
    detailTitleRow.height = 25;

    const detailsMap = [
      ['Employee ID', fieldOfficerDetails.employee_id || "-"],
      ['Officer Name', fieldOfficerDetails.name || "-"],
      ['Email Address', fieldOfficerDetails.email || "-"],
      ['Mobile Number', fieldOfficerDetails.mobile_no || "-"],
      ['Vendor Name', fieldOfficerDetails.vendor?.name || "-"]
    ];

    detailsMap.forEach((entry) => {
      const r = worksheet.addRow(['', entry[0], entry[1]]);
      r.height = 20;
      r.getCell(2).font = { bold: true };
      r.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: lightGreen } };
      r.getCell(3).alignment = { horizontal: 'left', indent: 1 };
    });

    worksheet.addRow([]);
    worksheet.addRow([]);

    const headerData = ['', 'Date', ...displaySlots.map(slot => formatSlotLabel(slot))];
    const headerRow = worksheet.addRow(headerData);
    headerRow.height = 35;

    headerRow.eachCell((cell, colNumber) => {
      if (colNumber > 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: brandColor } };
        cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 11 };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = {
          top: { style: 'medium' },
          left: borderStyle,
          bottom: { style: 'medium' },
          right: borderStyle
        };
      }
    });

    officerTracking.forEach(datedata => {
      let formattedRowDate = datedata.date;
      if (datedata.date && datedata.date.includes('-')) {
        const parts = datedata.date.split('-');
        if (parts.length === 3) formattedRowDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }

      const rowData = ['', formattedRowDate];
      const combinedTracking = getCombinedTracking(datedata);
      displaySlots.forEach(slot => {
        const [start, end] = slot.split(" to ");
        const track = getBestTrackInSlot(combinedTracking, start, end);
        if (track) {
          rowData.push(`${track.location || ''}\nLat: ${track.latitude}, Lon: ${track.longitude}\nTime: ${format12Hour(track.time)}`);
        } else {
          rowData.push("-");
        }
      });

      const row = worksheet.addRow(rowData);
      row.height = 70;
      row.eachCell((cell, colNumber) => {
        if (colNumber > 1) {
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          cell.border = {
            top: borderStyle,
            left: borderStyle,
            bottom: borderStyle,
            right: borderStyle
          };
        }
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const data = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
    const fileName = `FO_Tracking_${fieldOfficerDetails.name || 'Details'}_${fieldOfficerDetails.employee_id || ''}_${formatDateStr(new Date())}.xlsx`;
    saveAs(data, fileName);
  };

  const getLocationForTimeSlot = (dayTracking, slot, dayData) => {
    if (slot === "Tracking all day") {
      if (!dayTracking || dayTracking.length === 0) return "-";
      return (
        <button onClick={() => handleOpenMap(dayData)} style={{ padding: '6px 12px', fontWeight: 'bold', background: '#78c144', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Track On Map</button>
      );
    }
    const [start, end] = slot.split(" to ");
    const track = getBestTrackInSlot(dayTracking, start, end);

    return track ? (
      <div>
        {track.location && (
          <>
            {track.location.match(/.{1,50}/g)?.map((chunk, idx) => (
              <React.Fragment key={idx}>
                {chunk}
                <br />
              </React.Fragment>
            ))}
          </>
        )}
        <Tooltip title="location">
          <a
            style={{ textDecoration: "none", color: "#2B2B2BCC" }}
            href={`https://www.google.com/maps/place/${track.latitude}+${track.longitude}`}
            target="_blank"
            rel="noreferrer"
          >
            Lat-{track.latitude} <br />
            Lon-{track.longitude}
          </a>
        </Tooltip>
        <br />
        <span style={{ fontSize: '0.85em', color: '#888' }}>Time: {format12Hour(track.time)}</span>
      </div>
    ) : (
      "-"
    );
  };

  return (
    <div>
      <div style={{ padding: "20px 20px 0px 0px" }}>
        <Topbar />
      </div>
      <section style={{ padding: "20px 20px 0px 0px" }}>
        <div className="container-fluid">
          <div className="table_box">
            <TableContainer component={Paper}>
              <div className="table_inbox">
                <div className="add_user_list">
                  <div className="add_user_content">
                    <div
                      className="header-back"
                      style={{
                        fontFamily: "Poppins,sans-serif",
                        fontSize: "20px",
                        fontWeight: "bold",
                      }}
                    >
                      Field Officers Details
                      <button
                        className="back-btn"
                        onClick={() => navigate("/admin/FieldOfficers")}
                      >
                        Back
                      </button>
                    </div>
                    <div className="date-container">
                      <div className="dates">
                        <Grid container spacing={3}>
                          <Grid item md={6} className="customDatePicker">
                            <CustomDatePicker
                              label="Start Date "
                              value={startDate}
                              maxDate={new Date()}
                              onChange={(date) => {
                                const newStart = date ? date.toDate() : null;
                                setStartDate(newStart);
                                setPage(1);
                                if (newStart && endDate && newStart > endDate) {
                                  setEndDate(newStart);
                                }
                              }}
                            />
                          </Grid>
                          <Grid item md={6} className="customDatePicker">
                            <CustomDatePicker
                              label="End Date"
                              value={endDate}
                              minDate={startDate}
                              maxDate={new Date()}
                              onChange={(date) => {
                                const newEnd = date ? date.toDate() : null;
                                setEndDate(newEnd);
                                setPage(1);
                                if (newEnd && startDate && newEnd < startDate) {
                                  setStartDate(newEnd);
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </div>
                      <button
                        className="tracking-btn"
                        onClick={handleTrackFO}
                        style={{ marginRight: '10px' }}
                      >
                        Track FO
                      </button>
                      <button
                        className="tracking-btn"
                        onClick={handleExportExcel}
                        style={{ backgroundColor: '#78c144', display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        <SiMicrosoftexcel size={18} />
                        Export
                      </button>
                    </div>
                  </div>
                </div>
                <Table
                  className="adduser_table user_table product_table"
                  aria-label="simple table"
                >
                  <TableHead>
                    <TableRow className="table_head">
                      <TableCell align="center" className="second-child">
                        Employee ID
                      </TableCell>
                      <TableCell align="center" className="second-child">
                        Field Officer Name
                      </TableCell>
                      <TableCell align="center" className="third-child">
                        Email
                      </TableCell>
                      <TableCell align="center">Mobile</TableCell>
                      <TableCell align="center">Vendor Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className="table_body">
                    <TableRow key={fieldOfficerDetails._id}>
                      <TableCell align="center">
                        {fieldOfficerDetails.employee_id}
                      </TableCell>
                      <TableCell align="center">
                        {fieldOfficerDetails.name}
                      </TableCell>
                      <TableCell align="center">
                        {fieldOfficerDetails.email}
                      </TableCell>
                      <TableCell align="center">
                        {fieldOfficerDetails.mobile_no}
                      </TableCell>
                      <TableCell align="center">
                        {fieldOfficerDetails.vendor?.name}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </div>
        </div>
      </section>
      <section style={{ padding: "20px 20px 0px 0px" }}>
        <div className="container-fluid">
          <div className="table_box" ref={tableTrackingRef}>
            <TableContainer component={Paper} sx={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Table
                className="adduser_table user_table product_table"
                aria-label="simple table"
                style={{ whiteSpace: "nowrap", borderCollapse: "separate", minWidth: "1500px" }}
              >
                <TableHead>
                  <TableRow className="table_head">
                    <TableCell align="center"
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 10,
                        backgroundColor: "#f4f4f4",
                        borderRight: "1px solid #ddd",
                        minWidth: "120px"
                      }}
                      className="second-child">
                      Date
                    </TableCell>
                    {timeSlots.map((slot, index) => (
                      <TableCell
                        align="center"
                        key={index}
                        style={
                          slot === "Tracking all day"
                            ? {
                              position: "sticky",
                              right: 0,
                              zIndex: 10,
                              backgroundColor: "#f4f4f4",
                              borderLeft: "1px solid #ddd",
                              minWidth: "140px"
                            }
                            : {
                              borderRight: index === timeSlots.length - 2 ? undefined : "1px solid #82828299"
                            }
                        }
                      >
                        {formatSlotLabel(slot)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody className="table_body">
                  {officerTracking.map((datedata, i) => (
                    <TableRow key={datedata.date || i}>
                      <TableCell align="center"
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 5,
                          backgroundColor: "#fff",
                          borderRight: "1px solid #ddd"
                        }}
                      >
                        {formatDate(datedata.date)}
                      </TableCell>
                      {timeSlots.map((slot, index) => (
                        <TableCell
                          align="center"
                          style={
                            slot === "Tracking all day"
                              ? {
                                position: "sticky",
                                right: 0,
                                zIndex: 5,
                                backgroundColor: "#fff",
                                borderLeft: "1px solid #ddd"
                              }
                              : {
                                borderRight: index === timeSlots.length - 2 ? undefined : "1px solid #82828299"
                              }
                          }
                          key={index}
                        >
                          {getLocationForTimeSlot(
                            getCombinedTracking(datedata),
                            slot,
                            datedata
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          <div className="pagination_table">
            <Pagination
              shape="rounded"
              variant="outlined"
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handleChange}
            />
          </div>
          <MapModal />
        </div>
      </section>
    </div>
  );
}

export default FieldOfficersDetails;
